import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import { Curtain } from './components/Curtain';
import { TODO } from './components/TODO';
import { AuthLayout } from './layouts/AuthLayout';
import { PageViewProvider } from './providers/PageViewProvider';
import { RequireUserRoute } from './routes/RequireUserRoute';
import { ErrorScreen } from './screens/ErrorScreen';
import { TriggerErrorScreen } from './screens/TriggerErrorScreen';

export const router = createBrowserRouter([
  {
    element: (
      <PageViewProvider>
        <Outlet />
      </PageViewProvider>
    ),
    children: [
      {
        element: <AuthLayout />,
        errorElement: (
          <AuthLayout>
            <ErrorScreen />
          </AuthLayout>
        ),
        children: [
          {
            path: '/login',
            lazy: () => import('./screens/LoginScreen')
          },
          {
            path: '/switch',
            lazy: async () => {
              const { SwitchAccountScreen } = await import(
                './screens/SwitchAccountScreen'
              );
              return {
                Component: () => (
                  <RequireUserRoute>
                    <SwitchAccountScreen />
                  </RequireUserRoute>
                )
              };
            }
          }
        ]
      },
      {
        path: '/_dev/sounds',
        lazy: () => import('./screens/DevelopmentSoundColor')
      },
      {
        path: '/_dev/error',
        element: <TriggerErrorScreen />,
        errorElement: <ErrorScreen />
      },
      {
        path: '/',
        lazy: async () => {
          const { Layout } = await import('./layouts/Layout');
          return {
            Component: () => (
              <QueryParamProvider adapter={ReactRouter6Adapter}>
                <RequireUserRoute>
                  <Layout />
                </RequireUserRoute>
              </QueryParamProvider>
            ),
            errorElement: (
              <RequireUserRoute>
                <Layout>
                  <ErrorScreen />
                </Layout>
              </RequireUserRoute>
            )
          };
        },
        children: [
          {
            path: '/',
            lazy: () => import('./screens/StatusScreen')
          },
          {
            path: '/monitors/:monitorId/overview/:date',
            lazy: async () => {
              const { MonitorOverviewScreenRedirect } = await import(
                './screens/MonitorOverviewScreen'
              );
              return { Component: MonitorOverviewScreenRedirect };
            }
          },
          {
            path: '/monitors/:monitorId/overview',
            lazy: async () => {
              const { MonitorOverviewScreenRedirect } = await import(
                './screens/MonitorOverviewScreen'
              );
              return { Component: MonitorOverviewScreenRedirect };
            }
          },
          {
            path: '/data/by-monitor',
            lazy: () => import('./screens/DataViewMonitorScreen')
          },
          {
            path: '/data/by-sound',
            lazy: () => import('./screens/DataViewSoundScreen'),
            id: '/data/by-sound'
          },
          {
            path: '/data/response-time',
            lazy: () => import('./screens/DataViewResponseTimeScreen')
          },
          {
            path: '/acoustics/overview',
            lazy: () => import('./screens/AcousticsScreen'),
            children: [
              {
                path: '',
                id: 'overview',
                lazy: () => import('./screens/AcousticsOverviewScreen')
              },
              {
                path: 'monitors/:monitorId',
                id: 'monitors',
                lazy: () => import('./screens/AcousticsMonitorScreen')
              }
            ]
          },
          {
            path: '/climates',
            lazy: () => import('./screens/ClimatesScreen'),
            children: [
              {
                path: '',
                id: 'climates',
                lazy: () => import('./screens/ClimatesOverviewScreen')
              },
              {
                path: ':climateId',
                id: 'climatesSensor',
                lazy: () => import('./screens/ClimatesSensorScreen')
              }
            ]
          },
          {
            path: '/aid-calls/overview',
            lazy: () => import('./screens/AidCallOverviewScreen'),
            children: [
              {
                path: '',
                element: <Navigate to="/aid-calls/overview/calls" />
              },
              {
                path: 'calls',
                id: 'calls',
                lazy: () => import('./screens/AidCallCountScreen')
              },
              {
                path: 'responses',
                id: 'responses',
                lazy: () => import('./screens/AidCallResponseScreen')
              }
            ]
          },
          {
            path: '/insights/overall',
            element: (
              <TODO redirect={true}>
                <Curtain className="min-h-96" act="todo" />
              </TODO>
            )
          },
          {
            path: '/insights/by-monitor',
            element: (
              <TODO redirect={true}>
                <Curtain className="min-h-96" act="todo" />
              </TODO>
            )
          },
          {
            path: '/alerting',
            element: (
              <TODO redirect={true}>
                <Curtain className="min-h-96" act="todo" />
              </TODO>
            )
          },
          {
            path: '/account',
            element: (
              <TODO redirect={true}>
                <Curtain className="min-h-96" act="todo" />
              </TODO>
            )
          },
          {
            path: '/_dev/monitors/:monitorId',
            lazy: () => import('./screens/DevelopmentMonitorFirmwareDemoScreen')
          }
        ]
      },
      {
        path: '/',
        element: (
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <RequireUserRoute>
              <Outlet />
            </RequireUserRoute>
          </QueryParamProvider>
        ),
        errorElement: (
          <RequireUserRoute>
            <ErrorScreen />
          </RequireUserRoute>
        ),
        children: [
          {
            path: '/live',
            lazy: () => import('./screens/KanbanScreen')
          }
        ]
      }
    ]
  }
]);
